import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Avatar } from '../../../components/ui';
import { getThemeColors } from '../../../lib/theme';
import type { FamilyAccount } from '../../../lib/familyAccounts';
import { absenceGrundLabel } from '../../../lib/v1';
import { formatDate } from '../../../lib/dateFormat';
import { CareParamIconRow } from '../../../components/care/CareParamIcons';

/** Bild des Kontos = Foto der (ersten) betreuten Person. */
export function careRecipientUserId(acc: FamilyAccount): number | null {
  const m = acc.members?.find(x => x.is_care_recipient || x.is_pflegeempfaenger);
  return m?.user_id ?? null;
}

// ─── Zell-Renderer je Spalten-ID ────────────────────────────────────────────
export function renderAccountCell(
  acc: FamilyAccount,
  colId: string,
  dark: boolean,
  t: ReturnType<typeof getThemeColors>,
): React.ReactNode {
  switch (colId) {
    case 'account':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={acc.name || `#${acc.id}`} userId={careRecipientUserId(acc)} size={34} dark={dark} />
          <div>
            <div style={{ fontWeight: 600, color: t.heading }}>{acc.name || `Konto #${acc.id}`}</div>
            <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>#{acc.id}</div>
          </div>
        </div>
      );
    case 'care_recip':
      return acc.care_recipients?.length ? acc.care_recipients.join(', ') : '—';
    case 'members_count':
      return <Badge tone="neutral" dark={dark}>{acc.members_count}</Badge>;
    case 'pflegegrad':
      return <PflegegradCell account={acc} dark={dark} />;
    case 'care_icons':
      return (acc.care_icons?.length ?? 0) > 0
        ? <CareParamIconRow keys={acc.care_icons!} dark={dark} size={15} gap={5} />
        : <span style={{ color: t.muted }}>—</span>;
    case 'absence':
      return <AbsenceCell account={acc} dark={dark} />;
    // Batch-13 Paket C Nacharbeit — Betreuungskraft/Ansprechpartner des
    // primären Pflegeempfängers, inline vom v2-Listen-Endpunkt geliefert.
    // Ersetzt die entfernte "Status"-Spalte (PhaseCell).
    case 'caregiver':
      return <span>{typeof acc.bk_name === 'string' && acc.bk_name ? acc.bk_name : <span style={{ color: '#98a2b3' }}>—</span>}</span>;
    case 'contact_name':
      return <span>{typeof acc.contact_name === 'string' && acc.contact_name ? acc.contact_name : <span style={{ color: '#98a2b3' }}>—</span>}</span>;
    case 'sgb5':
      return acc.sgb5_active
        ? <Badge tone="brand" dark={dark}>SGB V</Badge>
        : <span style={{ color: '#98a2b3' }}>—</span>;
    default:
      return '—';
  }
}

/** Sammelt die Pflegegrade der Pflegeempfänger im Konto als Badges. */
export function PflegegradCell({ account, dark }: { account: FamilyAccount; dark: boolean }) {
  const grades = useMemo(() => {
    return (account.members ?? [])
      .filter(m => m.is_care_recipient && m.pflegegrad != null)
      .map(m => m.pflegegrad as number);
  }, [account.members]);
  if (grades.length === 0) return <span style={{ color: '#98a2b3' }}>—</span>;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {grades.map((g, i) => <Badge key={i} tone="brand" dark={dark}>PG {g}</Badge>)}
    </div>
  );
}

/** Zeigt die höchste Phase unter den Pflegeempfängern (oder —). */
export function PhaseCell({ account, dark }: { account: FamilyAccount; dark: boolean }) {
  const phase = useMemo(() => {
    const phases = (account.members ?? [])
      .filter(m => m.is_care_recipient && m.phase != null)
      .map(m => m.phase as number);
    return phases.length ? Math.max(...phases) : null;
  }, [account.members]);
  if (phase == null) return <span style={{ color: '#98a2b3' }}>—</span>;
  return <Badge tone="neutral" dark={dark}>Phase {phase}</Badge>;
}

/**
 * Abwesenheit im laufenden Monat inkl. Grund (z. B. Krankenhaus).
 *
 * Eine gerade laufende Abwesenheit wird hervorgehoben (Warnton) und mit ihrem
 * Enddatum gezeigt; eine Abwesenheit, die diesen Monat bereits beendet wurde
 * oder erst noch bevorsteht, erscheint gedämpft mit ihrem Zeitraum. Damit
 * deckt sich die Spalte mit dem Filter „Aktuell abwesend", der den ganzen
 * Monat abdeckt.
 */
export function AbsenceCell({ account, dark }: { account: FamilyAccount; dark: boolean }) {
  const { t: tr } = useTranslation();
  const a = account.absence ?? null;
  if (!a) return <span style={{ color: '#98a2b3' }}>—</span>;

  const fmt = (v?: string | null) => formatDate(v, '') || null;
  const grund = absenceGrundLabel(a.grund, tr) || '—';
  const from  = fmt(a.startdatum);
  const to    = fmt(a.enddatum);

  const text = a.running
    ? `${grund}${to ? ` · bis ${to}` : ` · ${tr('pflege.absences.open')}`}`
    : `${grund} · ${from && to ? `${from}–${to}` : from ? `ab ${from}` : tr('pflege.absences.open')}`;

  return (
    <div>
      <Badge tone={a.running ? 'warning' : 'neutral'} dark={dark}>{text}</Badge>
      {a.member && (
        <div style={{ fontSize: 11, color: '#98a2b3', marginTop: 2 }}>{a.member}</div>
      )}
    </div>
  );
}
